import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, MapPin, Calendar, TrendingUp, DollarSign,
  ArrowUpDown, ChevronDown, ChevronUp, BarChart3, Home,
  Layers, Shield, Eye, EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null) return "N/A";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const fmtNum = (n) => (n == null ? "N/A" : n.toLocaleString());

function StatBar({ label, values, format = "dollar", highlight }) {
  const nums = values.map((v) => v.value).filter((v) => v != null);
  if (!nums.length) return null;
  const max = Math.max(...nums);

  return (
    <div className="space-y-2" data-testid={`stat-bar-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
      {values.map((item, i) => {
        const pct = max > 0 && item.value != null ? (item.value / max) * 100 : 0;
        const isHighlight = highlight === "max" ? item.value === max : highlight === "min" ? item.value === Math.min(...nums) : false;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-28 text-xs text-gray-600 truncate font-medium">{item.name}</div>
            <div className="flex-1 h-7 bg-gray-100 rounded-md overflow-hidden relative">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className={`h-full rounded-md ${isHighlight ? "bg-emerald-500" : "bg-[#0066FF]/70"}`}
              />
              <span className="absolute inset-y-0 right-2 flex items-center text-xs font-semibold text-gray-700">
                {format === "dollar" ? fmt(item.value) : format === "percent" ? `${item.value}%` : fmtNum(item.value)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BuildingCard({ building, isSelected, onToggle, rank }) {
  const s = building.stats;
  const beds = Object.entries(building.bedroom_breakdown || {}).sort(([a], [b]) => {
    const order = { Studio: 0, "1BR": 1, "2BR": 2, "3BR": 3, "4BR": 4 };
    return (order[a] ?? 99) - (order[b] ?? 99);
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: rank * 0.1 }}
      className={`relative rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
        isSelected
          ? "border-[#0066FF] shadow-lg shadow-blue-100"
          : "border-gray-200 hover:border-gray-300"
      }`}
      data-testid={`building-card-${building.name.toLowerCase().replace(/\s/g, "-")}`}
    >
      {/* Image header */}
      <div className="relative h-44 bg-gray-900">
        {building.image ? (
          <img src={building.image} alt={building.name} className="w-full h-full object-cover opacity-80" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="w-16 h-16 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-xl font-bold text-white">{building.name}</h3>
          <div className="flex items-center gap-1 mt-1">
            <MapPin className="w-3.5 h-3.5 text-gray-300" />
            <span className="text-sm text-gray-300">{building.neighborhood}</span>
          </div>
        </div>
        <button
          onClick={() => onToggle(building.building_id)}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            isSelected ? "bg-[#0066FF] text-white" : "bg-white/20 backdrop-blur-sm text-white hover:bg-white/40"
          }`}
          data-testid={`toggle-compare-${building.name.toLowerCase().replace(/\s/g, "-")}`}
        >
          {isSelected ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      </div>

      {/* Key metrics */}
      <div className="p-5">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="text-xs text-gray-500 font-medium">Avg Price/SF</div>
            <div className="text-lg font-bold text-[#1A1A2E]">${fmtNum(s.ppsf_avg)}</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="text-xs text-gray-500 font-medium">Est. Cap Rate*</div>
            <div className="text-lg font-bold text-emerald-600">{s.cap_rate_avg}%</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="text-xs text-gray-500 font-medium">Price Range</div>
            <div className="text-sm font-bold text-[#1A1A2E]">{fmt(s.price_min)} - {fmt(s.price_max)}</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="text-xs text-gray-500 font-medium">Units Avail</div>
            <div className="text-lg font-bold text-[#0066FF]">{building.available_units}</div>
          </div>
        </div>

        {/* Quick info */}
        <div className="space-y-2 text-sm text-gray-600 mb-4">
          <div className="flex justify-between">
            <span>Built</span>
            <span className="font-semibold text-[#1A1A2E]">{building.year_built || "N/A"}</span>
          </div>
          <div className="flex justify-between">
            <span>Developer</span>
            <span className="font-semibold text-[#1A1A2E] text-right text-xs">{building.developer || "N/A"}</span>
          </div>
          <div className="flex justify-between">
            <span>Avg Monthly CC</span>
            <span className="font-semibold text-[#1A1A2E]">${fmtNum(s.avg_cc)}</span>
          </div>
          <div className="flex justify-between">
            <span>Avg Monthly Tax</span>
            <span className="font-semibold text-[#1A1A2E]">${fmtNum(s.avg_tax)}</span>
          </div>
          <div className="flex justify-between">
            <span>Avg Est. Rent</span>
            <span className="font-semibold text-emerald-600">${fmtNum(s.avg_rent)}/mo</span>
          </div>
        </div>

        {/* Bedroom breakdown */}
        <div className="border-t border-gray-100 pt-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Unit Mix</div>
          <div className="flex flex-wrap gap-2">
            {beds.map(([label, data]) => (
              <div key={label} className="bg-blue-50 text-[#0066FF] px-2.5 py-1 rounded-lg text-xs font-semibold">
                {label}: {data.count}
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function BuildingComparison({ embedded }) {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sortBy, setSortBy] = useState("ppsf");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    fetchBuildings();
  }, []);

  const fetchBuildings = async () => {
    try {
      const res = await axios.get(`${API}/buildings/compare`);
      setBuildings(res.data);
      // Select all by default
      setSelected(new Set(res.data.map((b) => b.building_id)));
    } catch (err) {
      logger.error("Failed to fetch buildings", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sortedBuildings = useMemo(() => {
    const copy = [...buildings];
    copy.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "ppsf": va = a.stats.ppsf_avg; vb = b.stats.ppsf_avg; break;
        case "cap": va = a.stats.cap_rate_avg; vb = b.stats.cap_rate_avg; break;
        case "price": va = a.stats.price_avg; vb = b.stats.price_avg; break;
        case "units": va = a.available_units; vb = b.available_units; break;
        default: va = a.stats.ppsf_avg; vb = b.stats.ppsf_avg;
      }
      if (va == null) return 1;
      if (vb == null) return -1;
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return copy;
  }, [buildings, sortBy, sortDir]);

  const selectedBuildings = useMemo(
    () => sortedBuildings.filter((b) => selected.has(b.building_id)),
    [sortedBuildings, selected]
  );

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("asc"); }
  };

  if (loading) {
    return (
      <div className={`${embedded ? "py-12" : "min-h-screen pt-20"} flex items-center justify-center`}>
        <div className="animate-pulse text-gray-400 text-lg">Loading buildings...</div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? "pb-8" : "min-h-screen bg-gray-50 pt-24 pb-16"}`} data-testid="building-comparison-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        {!embedded && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="flex items-center gap-2 text-[#0066FF] text-sm font-semibold uppercase tracking-wider mb-2">
            <BarChart3 className="w-4 h-4" />
            Building Comparison
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A2E] mb-3">
            Compare Property Details
          </h1>
          <p className="text-gray-500 max-w-2xl">
            Side-by-side analysis of our curated new development condo units across NYC.
            Compare property details and unit availability.
          </p>
        </motion.div>
        )}

        {/* Sort controls */}
        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="sort-controls">
          <span className="text-sm text-gray-500 font-medium">Sort by:</span>
          {[
            { key: "ppsf", label: "Price/SF" },
            { key: "cap", label: "Cap Rate" },
            { key: "price", label: "Avg Price" },
            { key: "units", label: "Units" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => toggleSort(key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                sortBy === key
                  ? "bg-[#0066FF] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
              data-testid={`sort-${key}`}
            >
              {label}
              {sortBy === key && (
                sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />
              )}
            </button>
          ))}
        </div>

        {/* Building cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-12">
          {sortedBuildings.map((b, i) => (
            <BuildingCard
              key={b.building_id}
              building={b}
              isSelected={selected.has(b.building_id)}
              onToggle={toggleSelect}
              rank={i}
            />
          ))}
        </div>

        {/* Comparison charts section */}
        {selectedBuildings.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-200 p-6 lg:p-8 shadow-sm"
            data-testid="comparison-charts"
          >
            <h2 className="text-xl font-bold text-[#1A1A2E] mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#0066FF]" />
              Head-to-Head Comparison
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <StatBar
                label="Average Price per SF"
                values={selectedBuildings.map((b) => ({ name: b.name, value: b.stats.ppsf_avg }))}
                format="dollar"
                highlight="min"
              />
              <StatBar
                label="Avg. Est. Cap Rate*"
                values={selectedBuildings.map((b) => ({ name: b.name, value: b.stats.cap_rate_avg }))}
                format="percent"
                highlight="max"
              />
              <StatBar
                label="Average Price"
                values={selectedBuildings.map((b) => ({ name: b.name, value: b.stats.price_avg }))}
                format="dollar"
              />
              <StatBar
                label="Average Estimated Rent"
                values={selectedBuildings.map((b) => ({ name: b.name, value: b.stats.avg_rent }))}
                format="dollar"
                highlight="max"
              />
              <StatBar
                label="Average Common Charges"
                values={selectedBuildings.map((b) => ({ name: b.name, value: b.stats.avg_cc }))}
                format="dollar"
                highlight="min"
              />
              <StatBar
                label="Available Units"
                values={selectedBuildings.map((b) => ({ name: b.name, value: b.available_units }))}
                format="number"
              />
            </div>
          </motion.div>
        )}

        {selectedBuildings.length < 2 && (
          <div className="text-center py-12 text-gray-400" data-testid="select-prompt">
            <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Select at least 2 buildings to compare</p>
            <p className="text-sm mt-1">Click the eye icon on building cards above</p>
          </div>
        )}

        {/* Detailed comparison table */}
        {selectedBuildings.length >= 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm"
            data-testid="comparison-table"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-5 py-4 font-semibold text-gray-500 uppercase text-xs tracking-wider">Metric</th>
                    {selectedBuildings.map((b) => (
                      <th key={b.building_id} className="text-center px-4 py-4 font-semibold text-[#1A1A2E] text-sm">
                        {b.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { label: "Neighborhood", get: (b) => b.neighborhood },
                    { label: "Year Built", get: (b) => b.year_built || "N/A" },
                    { label: "Available Units", get: (b) => b.available_units },
                    { label: "Price Range", get: (b) => `${fmt(b.stats.price_min)} - ${fmt(b.stats.price_max)}` },
                    { label: "Avg Price", get: (b) => fmt(b.stats.price_avg) },
                    { label: "Avg Price/SF", get: (b) => `$${fmtNum(b.stats.ppsf_avg)}` },
                    { label: "SF Range", get: (b) => `${fmtNum(b.stats.sqft_min)} - ${fmtNum(b.stats.sqft_max)}` },
                    { label: "Est. Cap Rate Range*", get: (b) => `${b.stats.cap_rate_min}% - ${b.stats.cap_rate_max}%` },
                    { label: "Avg Est. Cap Rate*", get: (b) => `${b.stats.cap_rate_avg}%`, highlight: true },
                    { label: "Avg Common Charges", get: (b) => `$${fmtNum(b.stats.avg_cc)}/mo` },
                    { label: "Avg Monthly Tax", get: (b) => `$${fmtNum(b.stats.avg_tax)}/mo` },
                    { label: "Avg Est. Rent", get: (b) => `$${fmtNum(b.stats.avg_rent)}/mo` },
                    { label: "Developer", get: (b) => b.developer || "N/A" },
                  ].map((row, i) => (
                    <tr key={i} className={`${row.highlight ? "bg-emerald-50/50" : i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                      <td className="px-5 py-3 font-medium text-gray-700">{row.label}</td>
                      {selectedBuildings.map((b) => (
                        <td key={b.building_id} className={`text-center px-4 py-3 ${row.highlight ? "font-bold text-emerald-600" : "text-gray-600"}`}>
                          {row.get(b)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Cap Rate Disclaimer */}
        <p className="text-xs text-gray-400 mt-6 text-center max-w-2xl mx-auto">
          *Cap rate is an estimated metric based on assumptions regarding income and expenses. It is provided for informational purposes only and does not represent actual or future performance. Results may vary.
        </p>

        {/* CTA */}
        {!embedded && (
        <div className="mt-12 text-center">
          <Link to="/properties">
            <Button className="bg-[#0066FF] hover:bg-[#0052CC] text-white px-8 py-3 rounded-xl text-sm font-semibold" data-testid="view-all-properties-btn">
              <Home className="w-4 h-4 mr-2" />
              View All Properties
            </Button>
          </Link>
        </div>
        )}
      </div>
    </div>
  );
}
